import { motion } from "framer-motion";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  CircleDollarSign,
  Flame,
  Landmark,
  PiggyBank,
  Target,
  TrendingDown,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import React from "react";
import { cn } from "@repo/core";
import { useCurrency } from "../../hooks/useCurrency";

/*
 * Accent token palette — used for non-KPI AppCards (forms, fund tiles, etc.).
 * KPI cards intentionally do NOT use full pastel fills (see KpiCard).
 */
const PASTEL: Record<string, { cls: string }> = {
  mint:     { cls: "bg-[#dcebe7] text-[#173e39] border-[#aec6c0] dark:bg-teal-900/25 dark:text-teal-100 dark:border-teal-800" },
  coral:    { cls: "bg-[#fdf0ed] text-[#7a2e1e] border-[#f0c0b0] dark:bg-rose-900/25 dark:text-rose-100 dark:border-rose-800" },
  lavender: { cls: "bg-[#eae7dc] text-[#5c716c] border-[#aec6c0] dark:bg-teal-900/20 dark:text-teal-200 dark:border-teal-800" },
  lemon:    { cls: "bg-amber-50 text-amber-900 border-amber-200 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-800" },
  sky:      { cls: "bg-[#cfe0db] text-[#173e39] border-[#7fa69e] dark:bg-teal-900/30 dark:text-teal-100 dark:border-teal-700" },
  white:    { cls: "bg-card text-card-foreground border-border" },
};

type PaletteColor = keyof typeof PASTEL;

/** Semantic accent for KPI badge + left bar (never a full card fill). */
const KPI_ACCENT: Record<
  string,
  { bar: string; badgeBg: string; badgeFg: string; DefaultIcon: LucideIcon }
> = {
  mint: {
    bar: "#22c55e",
    badgeBg: "bg-emerald-500/10 dark:bg-emerald-400/15",
    badgeFg: "text-emerald-600 dark:text-emerald-400",
    DefaultIcon: TrendingUp,
  },
  coral: {
    bar: "#ef4444",
    badgeBg: "bg-rose-500/10 dark:bg-rose-400/15",
    badgeFg: "text-rose-600 dark:text-rose-400",
    DefaultIcon: TrendingDown,
  },
  lemon: {
    bar: "#f59e0b",
    badgeBg: "bg-amber-500/10 dark:bg-amber-400/15",
    badgeFg: "text-amber-600 dark:text-amber-400",
    DefaultIcon: Target,
  },
  lavender: {
    bar: "hsl(var(--primary))",
    badgeBg: "bg-primary/10",
    badgeFg: "text-primary",
    DefaultIcon: Wallet,
  },
  sky: {
    bar: "#0d9488",
    badgeBg: "bg-teal-500/10 dark:bg-teal-400/15",
    badgeFg: "text-teal-700 dark:text-teal-300",
    DefaultIcon: CalendarDays,
  },
  white: {
    bar: "hsl(var(--primary))",
    badgeBg: "bg-primary/10",
    badgeFg: "text-primary",
    DefaultIcon: CircleDollarSign,
  },
};

// ── AppCard ───────────────────────────────────────────────
interface AppCardProps {
  children: React.ReactNode;
  color?: PaletteColor;
  className?: string;
  onClick?: () => void;
  padding?: "sm" | "md" | "lg" | "none";
}

export function AppCard({
  children,
  color = "white",
  className,
  onClick,
  padding = "md",
}: AppCardProps) {
  const { cls } = PASTEL[color] ?? PASTEL.white;
  const padMap  = { none: "", sm: "p-3", md: "p-4", lg: "p-6" };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      whileHover={onClick ? { y: -1 } : undefined}
      onClick={onClick}
      className={cn(
        "app-card border",
        cls,
        padMap[padding],
        onClick && "cursor-pointer select-none",
        className
      )}
    >
      {children}
    </motion.div>
  );
}

// ── KPI / Stat card (premium: neutral surface + accent bar + badge) ──
interface KpiCardProps {
  label:   string;
  value:   string | number;
  sub?:    string;
  color?:  PaletteColor;
  icon?:   React.ReactNode;
  change?: number;
  index?:  number;
  /** Optional footer (e.g. ProgressBar) */
  children?: React.ReactNode;
  size?:   "md" | "lg";
}

export function KpiCard({
  label,
  value,
  sub,
  color = "lavender",
  icon,
  change,
  index = 0,
  children,
  size = "md",
}: KpiCardProps) {
  const accent = KPI_ACCENT[color] ?? KPI_ACCENT.lavender;
  const DefaultIcon = accent.DefaultIcon;
  const valueClass =
    size === "lg"
      ? "text-3xl min-[390px]:text-4xl tracking-tight"
      : "text-2xl tracking-tight";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.3 }}
      className="h-full"
    >
      <div
        className={cn(
          "app-card border border-border bg-card text-card-foreground relative overflow-hidden h-full",
          size === "lg" ? "p-5 sm:p-6" : "p-4"
        )}
      >
        {/* Left accent bar — consistent app-wide */}
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-[3px]"
          style={{ backgroundColor: accent.bar }}
        />

        <div className="pl-1.5 flex flex-col h-full">
          <div className="flex items-start justify-between gap-3 mb-2">
            <p className="app-section-title mb-0 leading-tight pt-0.5">{label}</p>
            <div
              className={cn(
                "w-9 h-9 rounded-xl flex items-center justify-center shrink-0",
                accent.badgeBg,
                accent.badgeFg
              )}
            >
              {icon ?? <DefaultIcon size={16} strokeWidth={2} />}
            </div>
          </div>

          <p
            className={cn("text-foreground leading-none", valueClass)}
            style={{ fontFamily: "var(--font-display, inherit)", fontWeight: 400 }}
          >
            {value}
          </p>

          {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}

          {change !== undefined && (
            <p
              className={cn(
                "text-xs font-semibold mt-1.5 inline-flex items-center gap-0.5",
                change >= 0
                  ? "text-emerald-600 dark:text-emerald-400"
                  : "text-rose-500"
              )}
            >
              {change >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
              {Math.abs(change).toFixed(1)}%
            </p>
          )}

          {children && <div className="mt-3 pt-1">{children}</div>}
        </div>
      </div>
    </motion.div>
  );
}

/** Re-export common icons so pages can pass metric-specific badges. */
export const KpiIcons = {
  moneyIn: CircleDollarSign,
  moneyOut: ArrowDownRight,
  leftover: Wallet,
  savings: PiggyBank,
  rate: Target,
  assets: Landmark,
  debts: TrendingDown,
  calendar: CalendarDays,
  flame: Flame,
  up: TrendingUp,
} as const;

// ── Section heading ────────────────────────────────────────
export function SectionTitle({
  children,
  action,
  className,
}: {
  children:  React.ReactNode;
  action?:   React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-between mb-4", className)}>
      <h2 className="text-sm font-semibold text-foreground">{children}</h2>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  );
}

// ── Progress bar ───────────────────────────────────────────
interface ProgressBarProps {
  value:     number;
  color?:    string;
  label?:    string;
  showPct?:  boolean;
  height?:   number;
  className?: string;
}

export function ProgressBar({
  value,
  color,
  label,
  showPct = true,
  height  = 8,
  className,
}: ProgressBarProps) {
  const clamped = Math.min(100, Math.max(0, value));
  const over    = value > 100;
  const bgColor = over ? "hsl(var(--destructive))" : (color ?? "hsl(var(--primary))");

  return (
    <div className={cn("w-full", className)}>
      {(label || showPct) && (
        <div className="flex justify-between text-[11px] font-medium text-muted-foreground mb-1">
          {label && <span>{label}</span>}
          {showPct && (
            <span className={over ? "text-destructive font-bold" : undefined}>
              {Math.round(value)}%
            </span>
          )}
        </div>
      )}
      <div className="w-full rounded-full overflow-hidden bg-muted" style={{ height }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${clamped}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="h-full rounded-full"
          style={{ backgroundColor: bgColor }}
        />
      </div>
    </div>
  );
}

// ── Money display — respects active currency locale ────────
export function Money({
  amount,
  className,
  colored  = false,
  size     = "md",
  compact  = false,
}: {
  amount:   number;
  className?: string;
  colored?:  boolean;
  size?:    "sm" | "md" | "lg";
  compact?: boolean;
}) {
  const { format } = useCurrency();
  const isNeg = amount < 0;
  const sizeClass = { sm: "text-sm", md: "text-base", lg: "text-xl" }[size];

  return (
    <span
      className={cn(
        "font-medium tabular-nums",
        sizeClass,
        colored && (isNeg ? "text-[#c9503a]" : "text-[#2f7e78] dark:text-[#4fa89e]"),
        !colored && "text-foreground",
        className
      )}
    >
      {format(amount, compact)}
    </span>
  );
}
